import { createContext, useContext } from 'react';
import type { DashboardConfig } from '@trinity/chat/types/dashboard-config';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PageWithConfig {
  id: number;
  name: string;
  config: DashboardConfig | Record<string, unknown>;
}

export interface PageVersion {
  id: number;
  description?: string;
  createdAt: string;
}

export interface WidgetInstance {
  id: number;
  title: string;
  type: string;
  props?: unknown;
  sourceMetadata?: unknown;
}

// ─── Adapter interface ──────────────────────────────────────────────────────

/**
 * API adapter that each consuming app (frontend, chat) must implement.
 * Abstracts away the specific HTTP client / endpoint differences.
 */
export interface SegmentPageBuilderApi {
  getSegmentPage(projectId: number, segmentId: number): Promise<PageWithConfig | null>;
  savePageVersion(projectId: number, segmentId: number, data: { config: DashboardConfig; description?: string }): Promise<PageVersion>;
  revertPageVersion(projectId: number, segmentId: number, versionId: number): Promise<PageVersion>;
  getPageVersions?(projectId: number, segmentId: number): Promise<PageVersion[]>;
  /** Optional: load project widgets for the library picker */
  getWidgetsByProjectId?(projectId: number): Promise<WidgetInstance[]>;
}

// ─── React context ──────────────────────────────────────────────────────────

const SegmentPageBuilderApiContext = createContext<SegmentPageBuilderApi | null>(null);

export const SegmentPageBuilderApiProvider = SegmentPageBuilderApiContext.Provider;

export function useSegmentPageBuilderApi(): SegmentPageBuilderApi {
  const api = useContext(SegmentPageBuilderApiContext);
  if (!api) throw new Error('SegmentPageBuilderApiProvider is required');
  return api;
}
